// ─────────────────────────────────────────────────────────────
// KeywordGod — Shared Constants
// ─────────────────────────────────────────────────────────────

import type {
  CompetitionLevel,
  KeywordGrade,
  KeywordTier,
  SaturationGrade,
} from './types';

// ─── 등급 ────────────────────────────────────────────────────

export const GRADE_THRESHOLDS: Array<{ min: number; grade: KeywordGrade }> = [
  { min: 95, grade: 'S+' },
  { min: 90, grade: 'S' },
  { min: 85, grade: 'A+' },
  { min: 80, grade: 'A' },
  { min: 70, grade: 'B+' },
  { min: 60, grade: 'B' },
  { min: 50, grade: 'C+' },
  { min: 40, grade: 'C' },
  { min: 0, grade: 'D' },
];

export const SATURATION_THRESHOLDS: Array<{ min: number; grade: SaturationGrade }> = [
  { min: 50, grade: '매우높음' },
  { min: 30, grade: '높음' },
  { min: 10, grade: '보통' },
  { min: 5, grade: '낮음' },
  { min: 0, grade: '매우낮음' },
];

export const VERDICT_BY_GRADE: Record<KeywordGrade, string> = {
  'S+': '지금 바로 쓰세요! 최고의 키워드입니다',
  S: '지금 바로 쓰세요! 최고의 키워드입니다',
  'A+': '경쟁 낮음, 상위노출 가능성 높습니다',
  A: '경쟁 낮음, 상위노출 가능성 높습니다',
  'B+': '쓸만합니다. 콘텐츠 질로 승부하세요',
  B: '쓸만합니다. 콘텐츠 질로 승부하세요',
  'C+': '경쟁이 좀 있어요. 신중하게 판단하세요',
  C: '경쟁이 좀 있어요. 신중하게 판단하세요',
  D: '비추천. 다른 키워드를 찾아보세요',
};

// ─── 스코어링 ───────────────────────────────────────────────

/**
 * Phase 31: 검색량(VOLUME) 가중치 추가로 저용량 long-tail 함정 해소.
 * 합계 100 유지: 포화 25 + 꿀통 30 + 기회 20 + 검색량 25
 */
export const GOD_SCORE_WEIGHTS = {
  SATURATION: 25,
  HONEY: 30,
  OPPORTUNITY: 20,
  VOLUME: 25,
} as const;

export const HONEY_RAW_CEILING = 3.0;

/**
 * 검색량 점수 정규화 — log10 기반 0~100.
 * 10건 → 0, 100건 → 25, 1K → 50, 10K → 75, 100K+ → 100.
 */
export const VOLUME_SCORE_LOG_BASE = 100_000; // log10 = 5, normalize 분모 = (5-1) = 4

export const COMP_IDX_NUMERIC: Record<CompetitionLevel, number> = {
  낮음: 0.2,
  중간: 0.5,
  높음: 0.8,
};

// ─── 황금키워드 (DB 쿼리 조건과 동일) ───────────────────────

/**
 * 황금키워드 임계 — Phase 37 재조정 (Phase 31 산식 변경 반영).
 *
 * 이전 (산식: 포화30+꿀통40+기회30): god>=80 + sat<20 + search>=500 → ~100개 풀
 * 새 산식 (포화25+꿀통30+기회20+검색량25) 적용 후 god>=80 키워드가 15개로 급감.
 * 새 산식 기준 분포 분석:
 *   god>=65 + sat<30 + search>=500 → 101개 (이상적 풀)
 * 임계를 새 산식 분포에 맞춰 재조정.
 */
export const GOLDEN_KEYWORD = {
  /** god_score >= 65 (Phase 37: 80→65) */
  MIN_GOD_SCORE: 65,
  /** saturation < 30 (Phase 37: 20→30) */
  MAX_SATURATION: 30,
  /** total_search >= 500 (그대로) */
  MIN_MONTHLY_SEARCH: 500,
  /** 상위 N개 (Dashboard TOP 5 카드 용 — golden 페이지 자체는 LIMIT 100) */
  TOP_N: 20,
} as const;

// ─── 사용 한도 (챌린지 회원 무료 제공 — 안전망 한도만) ────

/** 일일 분석 안전 한도 (API 오남용 방지용; 회원 등급 무관) */
export const DAILY_ANALYZE_LIMIT = 1000;

/** 대량 분석 1회 최대 키워드 수 */
export const BULK_MAX = 100;

/** 키워드 DB 전체 상한 (자연 BFS 누적의 안전망) */
export const MAX_KEYWORDS = 10_000_000;

/** /api/stats/keywords 응답 캐시 TTL (초) — KV 최소 TTL 60. BFS 시점에 incremental update 로 실시간 유지 */
export const STATS_CACHE_TTL = 60;

/** queue-consumer / analyze 에서 발견 키워드를 keywords 테이블에 누적할 최대 개수 (1회 분석당) */
export const BFS_DISCOVER_MAX = 800;

/** 분석 시 related_keywords 테이블에 저장할 상위 N개 (D1 writes 절약) */
export const RELATED_TOP_N = 5;

// ─── 캐시 TTL (초) ──────────────────────────────────────────

export const CACHE_TTL = {
  /** 키워드 분석 결과 (KV: kw:{keyword}) — 6시간 */
  KEYWORD_ANALYSIS: 60 * 60 * 6,
  /** DB row 신선도 컷오프도 동일 — 6시간 이내면 DB만 사용, 그 이상이면 재호출 */
  DB_FRESH: 60 * 60 * 6,
  /** 황금키워드 (KV: golden:today) — 24시간 */
  GOLDEN_TODAY: 60 * 60 * 24,
  AUTOCOMPLETE: 60 * 60,
  SESSION_DAYS: 30,
} as const;

/** 세션 쿠키 maxAge (초) */
export const SESSION_MAX_AGE_SECONDS = CACHE_TTL.SESSION_DAYS * 24 * 60 * 60;

// ─── 키 풀 ──────────────────────────────────────────────────

export const KEY_POOL_SIZE = {
  ADS: 4,
  SEARCH: 40,
} as const;

export const SEARCH_DAILY_QUOTA = 25_000;

// ─── 자동 갱신 시스템 ───────────────────────────────────────

/** tier 별 다음 갱신 간격 (시간 단위). Phase 56: 신선도 강화로 단축. */
export const TIER_REFRESH_HOURS: Record<KeywordTier, number> = {
  S: 3,
  A: 12,
  B: 48,
  C: 120,
  D: 480,
};

/** tier 우선순위 (작을수록 먼저). cron 5분 SELECT의 ORDER BY 매핑 */
export const TIER_PRIORITY: Record<KeywordTier, number> = {
  S: 1,
  A: 2,
  B: 3,
  C: 4,
  D: 5,
};

export const TIER_ORDER: KeywordTier[] = ['D', 'C', 'B', 'A', 'S'];

/**
 * cron 1회에 큐로 보낼 최대 키워드 수.
 * Phase 20: 컨슈머 처리율(~250/2분)에 맞춰 2000→300 으로 축소.
 * 과다 enqueue 시 큐에 스테일 중복 메시지가 누적되어 컨슈머가 백로그에
 * 갇히는 문제(분석 완료 키워드만 재처리)가 있었음 → enqueue ≈ drain 으로 정렬.
 */
export const REFRESH_BATCH_LIMIT = 300;

/** Queue consumer 한 묶음 크기 (wrangler.toml max_batch_size와 동기화) */
export const QUEUE_BATCH_SIZE = 25;

/** 승격 임계 (둘 중 하나라도 충족) */
export const TIER_PROMOTION = {
  MIN_VIEW_COUNT_24H: 50,
  MIN_CHANGE_RATE: 0.2,
} as const;

/** 강등 임계 (모두 충족) */
export const TIER_DEMOTION = {
  /** view_count_24h == 0 이 며칠 연속 */
  ZERO_VIEW_DAYS: 7,
  /** change_rate < MIN_CHANGE_RATE 가 N회 연속 */
  MIN_CHANGE_RATE: 0.05,
  LOW_CHANGE_STREAK: 3,
} as const;

// ─── API 라우트 (프론트/백엔드 동기화) ───────────────────────

export const API_ROUTES = {
  AUTH: {
    GOOGLE_START: '/api/auth/google',
    GOOGLE_CALLBACK: '/api/auth/callback',
    ME: '/api/auth/me',
    LOGOUT: '/api/auth/logout',
  },
  KEYWORD: {
    ANALYZE: '/api/keyword/analyze', // GET ?q=
    BULK: '/api/keyword/bulk', // POST
  },
  DISCOVER: { INDEX: '/api/discover' },
  RANK: { CHECK: '/api/rank/check', HISTORY: '/api/rank/history' },
  GOLDEN: { TODAY: '/api/golden' },
  ME: {
    HISTORY: '/api/me/history',
    FAVORITES: '/api/me/favorites',
  },
} as const;
